/**
 * Seçili vekalet personasının istemci tarafındaki tek sahibi (089).
 *
 * <p><b>Neden oturum çerezi?</b> Gereksinim iki şeyi birden ister: seçim **tüm sekmelerde aynı**
 * olmalı, ama **yeni bir oturumda hatırlanmamalı**. Tek bir tarayıcı deposu bunu karşılamıyor:
 * `localStorage` sekmeler arası ortaktır ama tarayıcı kapanınca silinmez (OIDC oturumu da orada
 * durduğundan kullanıcı geri döndüğünde persona yaşardı); `sessionStorage` kapanışta silinir ama
 * **sekme başına** ayrıdır — iki sekmede farklı kimlikle çalışmak tam olarak kaçınılmak istenen şey.
 * Süre verilmeyen bir çerez ("session cookie") ikisini birden sağlar: aynı tarayıcı profilindeki tüm
 * sekmeler paylaşır, tarayıcı kapanınca kaybolur.
 *
 * <p><b>Çerez backend'e gönderilmez</b> — yalnız bu modül okur, değeri `apiClient` bir istek başlığına
 * yazar. Böylece THY'deki ayrı-domain kurulumunda `SameSite=None`/`withCredentials` gerekmez ve
 * backend cookie'siz (STATELESS) kalır.
 *
 * <p>Değer bir kimlik **iddiasıdır**, kanıt değil: sunucu her istekte vekaletin hâlâ geçerli olduğunu
 * kendi doğrular. Bu yüzden çereze müdahale eden biri kendisine verilmemiş bir erişim kazanamaz.
 */

/**
 * Çerez adı. `appStorageKey()`'in `ceosuite:` ön eki **kullanılamaz**: iki nokta, çerez adı için
 * geçerli bir karakter değildir (RFC 6265 token). Aynı ön eki tire ile taşır.
 */
const COOKIE_NAME = 'ceosuite-acting-user'

/** Oturum çerezi: `max-age`/`expires` **verilmez** — verilirse tarayıcı kapanışında silinmez. */
const COOKIE_ATTRIBUTES = 'Path=/; SameSite=Strict'

/** Geçmiş tarihli bir son kullanma, çerezi silmenin standart yoludur. */
const EXPIRED = 'Thu, 01 Jan 1970 00:00:00 GMT'

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const prefix = `${name}=`
  const found = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix))
  return found ? decodeURIComponent(found.slice(prefix.length)) : null
}

/** Seçili personanın kullanıcı kimliği; kendi personasında `null`. */
export function readActingUserId(): number | null {
  const raw = readCookie(COOKIE_NAME)
  if (raw == null || raw.trim() === '') return null
  const value = Number(raw)
  // Bozuk bir değer sessizce "kendi personası" sayılmaz — kalıntı temizlenir ki bir sonraki istek
  // sunucuya geçersiz bir başlık taşımasın.
  if (!Number.isInteger(value) || value <= 0) {
    clearActingUserId()
    return null
  }
  return value
}

/** Personayı seçer (oturum boyunca, tüm sekmelerde). */
export function saveActingUserId(userId: number): void {
  if (typeof document === 'undefined') return
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(String(userId))}; ${COOKIE_ATTRIBUTES}`
}

/** Kendi personasına döner (çıkışta ve geçersiz vekalette de çağrılır). */
export function clearActingUserId(): void {
  if (typeof document === 'undefined') return
  document.cookie = `${COOKIE_NAME}=; ${COOKIE_ATTRIBUTES}; Expires=${EXPIRED}`
}

/** Testlerin ve `clearAppSession`'ın adı elle yazmak zorunda kalmaması için. */
export const ACTING_USER_COOKIE_NAME = COOKIE_NAME
